import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Building2,
  MapPin,
  Upload,
  FileText,
  Plus,
  Pencil,
  Check,
  X,
  Download,
  Trash2,
} from "lucide-react";
import { API, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Spinner } from "../components/common";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const BLANK = {
  name: "",
  location: "",
  property_type: "",
  price_range: "",
  possession: "",
  description: "",
  image: "",
  configurations: "",
  amenities: "",
  highlights: "",
};
const list = (v) =>
  v
    .split(/,|\n/)
    .map((x) => x.trim())
    .filter(Boolean);
const toForm = (p = {}) => ({
  ...BLANK,
  ...p,
  configurations: (p.configurations || []).join(", "),
  amenities: (p.amenities || []).join(", "),
  highlights: (p.highlights || []).join("\n"),
});
const toPayload = (f) => ({
  ...f,
  configurations: list(f.configurations),
  amenities: list(f.amenities),
  highlights: list(f.highlights),
});
const inputClass =
  "mt-1 w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200";

export default function Projects() {
  const { isAdmin } = useAuth();
  const [projects, setProjects] = useState(null);
  const [requests, setRequests] = useState([]);
  const [active, setActive] = useState(null);
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const load = useCallback(async () => {
    const { data } = await API.get("/projects");
    setProjects(data);
    if (isAdmin)
      API.get("/project-change-requests").then((r) => setRequests(r.data));
  }, [isAdmin]);
  useEffect(() => {
    load().catch(() => setProjects([]));
  }, [load]);
  const edit = (p = {}) => {
    setEditor(p);
    setForm(toForm(p));
    setMediaFiles([]);
    setActive(null);
  };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Project name is required");
    setSaving(true);
    try {
      const { data } = editor?.id
        ? await API.put(`/projects/${editor.id}`, toPayload(form))
        : await API.post("/projects", toPayload(form));
      let uploaded = 0;
      const failed = [];
      if (isAdmin && data.id && mediaFiles.length) {
        for (const file of mediaFiles) {
          try {
            const fd = new FormData();
            fd.append("file", file);
            await API.post(`/projects/${data.id}/media`, fd);
            uploaded += 1;
          } catch (err) {
            failed.push(`${file.name}: ${apiError(err)}`);
          }
        }
      }
      setEditor(null);
      await load();
      if (failed.length) {
        toast.warning(
          `Project saved. ${uploaded} file(s) uploaded; ${failed.length} failed. Open the project to retry.`,
        );
      } else {
        toast.success(
          data.pending_approval
            ? "Submitted for admin approval"
            : `Project ${editor?.id ? "updated" : "created"}${uploaded ? ` with ${uploaded} media file(s)` : ""}`,
        );
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };
  const review = async (id, decision) => {
    try {
      await API.post(`/project-change-requests/${id}/${decision}`);
      toast.success(`Request ${decision}d`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const uploadMedia = async (pid, e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await API.post(`/projects/${pid}/media`, fd);
      }
      toast.success(
        `${files.length} media file${files.length === 1 ? "" : "s"} uploaded`,
      );
      const { data } = await API.get("/projects");
      setProjects(data);
      setActive(data.find((p) => p.id === pid));
    } catch (err) {
      toast.error(apiError(err));
    }
    e.target.value = "";
  };
  const deleteMedia = async (pid, id) => {
    try {
      await API.delete(`/projects/${pid}/media/${id}`);
      toast.success("Media removed");
      const { data } = await API.get("/projects");
      setProjects(data);
      setActive(data.find((p) => p.id === pid));
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const deleteProject = async (p) => {
    if (
      !window.confirm(
        `Delete “${p.name}” permanently? This also removes its uploaded media and project knowledge. This cannot be undone.`,
      )
    )
      return;
    try {
      await API.delete(`/projects/${p.id}`);
      toast.success("Project deleted");
      setActive(null);
      setEditor(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const downloadDetails = async (p) => {
    try {
      const { data } = await API.get(`/projects/${p.id}/export`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${p.name.replace(/[^a-z0-9]+/gi, "-")}-details.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  if (!projects) return <Spinner />;
  return (
    <div>
      <PageHeader
        title="RAV Projects"
        subtitle="Approved project knowledge powering the AI sales partner."
        testid="projects-header"
      >
        <button
          onClick={() => edit()}
          className="btn-gold px-4 py-2 flex items-center gap-2 text-sm"
          data-testid="add-project-btn"
        >
          <Plus className="h-4 w-4" />
          {isAdmin ? "Add Project" : "Propose Project"}
        </button>
      </PageHeader>
      {isAdmin && requests.length > 0 && (
        <div className="mb-card p-4 mb-6 border-amber-500/20">
          <div className="font-heading font-semibold text-slate-100 mb-3">
            Pending project approvals ({requests.length})
          </div>
          <div className="space-y-2">
            {requests.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between gap-3 bg-slate-900/50 border border-slate-800 rounded-lg p-3 text-sm"
              >
                <div>
                  <span className="text-slate-200">
                    {r.proposed_data?.name}
                  </span>
                  <span className="text-slate-500">
                    {" "}
                    · {r.action} by {r.submitted_by_name}
                  </span>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => review(r.id, "approve")}
                    className="text-emerald-400"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => review(r.id, "reject")}
                    className="text-red-400"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {projects.length === 0 ? (
        <div className="mb-card p-12 text-center">
          <Building2 className="h-10 w-10 text-slate-600 mx-auto mb-3" />
          <div className="text-slate-300">No projects added yet</div>
          <div className="text-sm text-slate-500 mb-4">
            Add verified project data before using AI with real leads.
          </div>
          <button onClick={() => edit()} className="btn-gold px-4 py-2 text-sm">
            {isAdmin ? "Add first project" : "Propose first project"}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {projects.map((p) => (
            <div
              key={p.id}
              className="mb-card mb-card-hover overflow-hidden cursor-pointer"
              onClick={() => setActive(p)}
            >
              <div className="h-44 bg-gradient-to-br from-slate-800 to-slate-900 relative overflow-hidden">
                {p.image ? (
                  <img
                    src={p.image}
                    alt={p.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Building2 className="h-12 w-12 text-slate-700 absolute inset-0 m-auto" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-transparent" />
                <span className="absolute bottom-2 left-3 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">
                  {p.property_type || "Project"}
                </span>
              </div>
              <div className="p-4">
                <div className="flex justify-between">
                  <h3 className="font-heading font-semibold text-slate-100">
                    {p.name}
                  </h3>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      edit(p);
                    }}
                    className="text-slate-500 hover:text-amber-400"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
                <div className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                  <MapPin className="h-3 w-3" />
                  {p.location || "Location not added"}
                </div>
                <div className="text-sm text-amber-400 mt-2">
                  {p.price_range || "Pricing to be confirmed"}
                </div>
                <div className="text-[11px] text-slate-500 mt-2 flex gap-1">
                  <FileText className="h-3 w-3" />
                  {(p.documents?.length || 0) +
                    (p.media?.filter((m) => m.kind === "brochure").length || 0)}{" "}
                  approved docs · {p.media?.length || 0} media
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog
        open={editor !== null}
        onOpenChange={(o) => !o && setEditor(null)}
      >
        <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editor?.id ? "Edit" : "Add"} Project</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              ["name", "Project name *"],
              ["location", "Location"],
              ["property_type", "Property type"],
              ["price_range", "Price range"],
              ["possession", "Possession"],
            ].map(([k, l]) => (
              <label key={k} className="text-xs text-slate-400">
                {l}
                <input
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className={inputClass}
                />
              </label>
            ))}
            <label className="sm:col-span-2 text-xs text-slate-400">
              Description
              <textarea
                rows="4"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                className={inputClass}
              />
            </label>
            <label className="sm:col-span-2 text-xs text-slate-400">
              Configurations (comma separated)
              <input
                value={form.configurations}
                onChange={(e) =>
                  setForm({ ...form, configurations: e.target.value })
                }
                className={inputClass}
              />
            </label>
            <label className="sm:col-span-2 text-xs text-slate-400">
              Amenities (comma separated)
              <input
                value={form.amenities}
                onChange={(e) =>
                  setForm({ ...form, amenities: e.target.value })
                }
                className={inputClass}
              />
            </label>
            <label className="sm:col-span-2 text-xs text-slate-400">
              Highlights (one per line)
              <textarea
                rows="3"
                value={form.highlights}
                onChange={(e) =>
                  setForm({ ...form, highlights: e.target.value })
                }
                className={inputClass}
              />
            </label>
            {isAdmin && (
              <label className="sm:col-span-2 text-xs text-slate-400">
                Project media
                <div className="mt-1 border border-dashed border-slate-700 rounded-lg p-4 text-center">
                  <Upload className="h-5 w-5 text-amber-400 mx-auto mb-1" />
                  <div className="text-sm text-slate-300">
                    Select brochures, images or videos
                  </div>
                  <input
                    type="file"
                    multiple
                    accept="application/pdf,image/*,video/mp4,video/webm,video/quicktime"
                    onChange={(e) => setMediaFiles([...e.target.files])}
                    className="mt-2 text-xs text-slate-400"
                  />
                  {mediaFiles.length > 0 && (
                    <div className="text-xs text-emerald-400 mt-2">
                      {mediaFiles.length} file(s) ready to upload
                    </div>
                  )}
                </div>
              </label>
            )}
          </div>
          <DialogFooter>
            <button
              onClick={save}
              disabled={saving}
              className="btn-gold px-4 py-2 text-sm"
            >
              {saving
                ? "Saving & uploading…"
                : isAdmin
                  ? "Save Project"
                  : "Submit for Approval"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between gap-3 pr-7">
                  <DialogTitle>{active.name}</DialogTitle>
                  <div className="flex gap-2">
                    <button
                      onClick={() => downloadDetails(active)}
                      className="px-3 py-1.5 rounded-lg border border-slate-700 text-xs text-slate-300 flex gap-1.5"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Details
                    </button>
                    <button
                      onClick={() => edit(active)}
                      className="px-3 py-1.5 rounded-lg border border-amber-500/30 text-xs text-amber-300 flex gap-1.5"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => deleteProject(active)}
                        className="px-3 py-1.5 rounded-lg border border-red-500/30 text-xs text-red-300 flex gap-1.5"
                        data-testid="delete-project-btn"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </DialogHeader>
              {active.image && (
                <img
                  src={active.image}
                  alt={active.name}
                  className="w-full h-52 object-cover rounded-lg"
                />
              )}
              <p className="text-sm text-slate-300">{active.description}</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-slate-500 text-xs">Price</span>
                  <div>{active.price_range}</div>
                </div>
                <div>
                  <span className="text-slate-500 text-xs">Possession</span>
                  <div>{active.possession}</div>
                </div>
                <div>
                  <span className="text-slate-500 text-xs">Configurations</span>
                  <div>{active.configurations?.join(", ")}</div>
                </div>
                <div>
                  <span className="text-slate-500 text-xs">Location</span>
                  <div>{active.location}</div>
                </div>
              </div>
              <div>
                <div className="caption-mono mb-2">Project Media</div>
                {!active.media?.length ? (
                  <div className="text-xs text-slate-500">
                    No uploaded media yet.
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {active.media.map((m) => (
                      <div
                        key={m.id}
                        className="bg-slate-900/60 border border-slate-800 rounded-lg overflow-hidden"
                      >
                        {m.kind === "image" ? (
                          <img
                            src={m.url}
                            alt={m.name}
                            className="w-full h-32 object-cover"
                          />
                        ) : m.kind === "video" ? (
                          <video
                            src={m.url}
                            controls
                            className="w-full h-32 bg-black"
                          />
                        ) : (
                          <div className="h-24 flex items-center justify-center">
                            <FileText className="h-8 w-8 text-red-400" />
                          </div>
                        )}
                        <div className="p-2 flex items-center gap-2">
                          <span className="flex-1 text-xs text-slate-300 truncate">
                            {m.name}
                          </span>
                          <a
                            href={`${m.url}?download=true`}
                            className="text-blue-400"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                          {isAdmin && (
                            <button
                              onClick={() => deleteMedia(active.id, m.id)}
                              className="text-red-400"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {isAdmin && (
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-500/30 text-xs text-amber-400 cursor-pointer">
                  <Upload className="h-4 w-4" />
                  Add brochure, images or videos
                  <input
                    type="file"
                    multiple
                    accept="application/pdf,image/*,video/mp4,video/webm,video/quicktime"
                    className="hidden"
                    onChange={(e) => uploadMedia(active.id, e)}
                  />
                </label>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
